/**
 * viewedAlerts — persists which alert IDs the user has "marked as viewed".
 *
 * The bell-icon badge over the navbar shows the count of CURRENTLY ACTIVE
 * alerts whose IDs are NOT in this viewed set. Marking-all-as-viewed adds
 * every visible alert ID to the set so the badge clears, but the alerts
 * themselves stay in the bell sheet so the user can still review them.
 *
 * Storage:
 *   • Keyed per user id so different accounts on the same device don't
 *     stomp on each other's badges.
 *   • Stored as a JSON array (Set isn't directly JSON-able).
 *   • Stale IDs auto-clean: callers pass the currently-visible alert IDs,
 *     and we drop anything from the viewed set that isn't visible anymore
 *     (so the next time those exact alerts come back, they re-badge).
 *
 * Why localStorage (no backend):
 *   "Viewed" is a UX hint, not a permission state. Persisting per-device
 *   keeps it instant and avoids spamming the API on every bell toggle. If
 *   we ever want cross-device sync we can promote it to the user document
 *   later — the API surface stays the same.
 */
const keyFor = (userId) => `qfresh_viewed_alerts_${userId || "anon"}`;

export const getViewedAlertIds = (userId) => {
  try {
    const raw = localStorage.getItem(keyFor(userId));
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return new Set(Array.isArray(arr) ? arr : []);
  } catch {
    return new Set();
  }
};

const writeViewedAlertIds = (userId, set) => {
  try {
    localStorage.setItem(keyFor(userId), JSON.stringify(Array.from(set)));
  } catch {
    /* localStorage full / disabled — fine to swallow */
  }
};

/**
 * Mark the given alert IDs as viewed. Pre-existing viewed IDs are kept
 * (so multi-tab sessions don't lose state) — caller is responsible for
 * calling `pruneViewedAlertIds` whenever they have a fresh full list.
 */
export const markAlertsViewed = (userId, ids) => {
  if (!ids || ids.length === 0) return;
  const current = getViewedAlertIds(userId);
  ids.forEach((id) => current.add(id));
  writeViewedAlertIds(userId, current);
};

/**
 * Drop any IDs from the viewed set that aren't in the supplied currently-
 * visible list. Lets us avoid the set growing forever, and ensures that if
 * a past-due alert later re-appears (e.g. user un-discards a container),
 * it'll badge again as unread.
 */
export const pruneViewedAlertIds = (userId, visibleIds) => {
  const current = getViewedAlertIds(userId);
  if (current.size === 0) return current;
  const visible = new Set(visibleIds);
  const next = new Set();
  current.forEach((id) => {
    if (visible.has(id)) next.add(id);
  });
  if (next.size !== current.size) writeViewedAlertIds(userId, next);
  return next;
};

/**
 * Returns the number of alerts whose IDs are NOT yet in the viewed set —
 * i.e. the number to render in the bell badge.
 */
export const unreadAlertCount = (userId, alerts) => {
  if (!alerts || alerts.length === 0) return 0;
  const viewed = getViewedAlertIds(userId);
  let n = 0;
  for (const a of alerts) {
    if (!viewed.has(a.id)) n += 1;
  }
  return n;
};
